import i18next from "i18next";

import { Models } from "api/types";

type CampaignCardPresentation = {
  title?: string | null;
  shortDescription?: string | null;
  description?: string | null;
  imageUrl?: string | null;
};

type PromotionWithCampaignPresentation = Models<"PromotionModel"> & {
  campaignCardPresentation?: CampaignCardPresentation | null;
  isAvailableToPlatformUser?: boolean;
};

export const getCampaignCardPresentation = (promotion: Models<"PromotionModel">) =>
  (promotion as PromotionWithCampaignPresentation).campaignCardPresentation ?? null;

export const getPromotionCardImageUrl = (promotion: Models<"PromotionModel">) =>
  getCampaignCardPresentation(promotion)?.imageUrl ?? promotion.imageUrl;

export const getPromotionCardTitle = (promotion: Models<"PromotionModel">) =>
  getCampaignCardPresentation(promotion)?.title ?? promotion.title;

export const getPromotionCardDescription = (promotion: Models<"PromotionModel">) =>
  getCampaignCardPresentation(promotion)?.description ??
  getCampaignCardPresentation(promotion)?.shortDescription ??
  promotion.shortDescription;

export const getPromotionDetailDescription = (promotion: Models<"PromotionModel">) =>
  getCampaignCardPresentation(promotion)?.description ?? promotion.description;

export const isPromotionAvailableToPlatformUser = (promotion: Models<"PromotionModel">) => {
  const value = (promotion as PromotionWithCampaignPresentation).isAvailableToPlatformUser;
  if (value == null) {
    throw new Error("Promotion availability is required");
  }
  return value;
};

const getPromoCodeRequiredMembershipIds = (promoCode: Models<"PromoCodeModel">) => {
  const membershipIds = promoCode.activeSubscriptionRequirementMembershipIds;
  if (membershipIds == null) {
    throw new Error("Promotion membership requirements are required");
  }
  return membershipIds;
};

export const getRequiredMembershipIds = (promotion: Models<"PromotionModel">) =>
  Array.from(new Set(promotion.promoCodes.flatMap(getPromoCodeRequiredMembershipIds)));

export const getSingleRequiredMembershipId = (promotion: Models<"PromotionModel">) => {
  const membershipIds = getRequiredMembershipIds(promotion);
  return membershipIds.length === 1 ? membershipIds[0] : null;
};

export const hasMembershipRequirement = (promotion: Models<"PromotionModel">) =>
  getRequiredMembershipIds(promotion).length > 0;

/**
 * Whether the campaign carries exactly one offer, so its promotion-level copy already says
 * everything the per-offer list would: a single-offer promotion has nothing to list.
 */
export const isSingleOfferPromotion = (promotion: Models<"PromotionModel">) =>
  promotion.promoCodes.length === 1;

/** An offer withheld behind a membership requirement, whoever is looking at it. */
export const isGatedCampaignOffer = (promoCode: Models<"PromoCodeModel">) =>
  getPromoCodeRequiredMembershipIds(promoCode).length > 0;

const getGatedOfferCount = (promotion: Models<"PromotionModel">) =>
  promotion.promoCodes.filter(isGatedCampaignOffer).length;

/**
 * A campaign whose offers disagree about membership — some gated, some open. Nothing above the
 * offer list can state that shape, so the list itself is what discloses it: a mixed campaign mounts
 * the per-offer badges even when it has no eligible in-app offer to apply, because the gated
 * sibling's requirement would otherwise go unsaid.
 */
export const isMixedMembershipPromotion = (promotion: Models<"PromotionModel">) => {
  const gatedOfferCount = getGatedOfferCount(promotion);
  return gatedOfferCount > 0 && gatedOfferCount < promotion.promoCodes.length;
};

/**
 * "Members-Only" is a statement about the whole promotion, so it holds only where the promotion is
 * one gated offer. A campaign carrying several offers gets no badge at all — whatever its mix, a
 * single word on the card would speak for offers it does not describe — and the per-offer badges
 * inside the detail page carry the membership story instead.
 */
export const getMembershipPromotionBadge = (promotion: Models<"PromotionModel">) =>
  isSingleOfferPromotion(promotion) && hasMembershipRequirement(promotion)
    ? i18next.t("promotionPresentation.membersOnlyBadge")
    : null;

/**
 * `startsOn`/`expiresOn` carry the window the offer is actually live in — for a campaign that is
 * the surviving offer's own window, not the campaign's. A promotion can therefore be returned by
 * the API while sitting outside that window, so both edges are read here rather than assumed open.
 */
export const isPromotionNotYetStarted = (promotion: Models<"PromotionModel">) =>
  promotion.startsOn != null && Date.parse(promotion.startsOn) >= Date.now();

export const isPromotionExpired = (promotion: Models<"PromotionModel">) =>
  promotion.expiresOn != null && Date.parse(promotion.expiresOn) <= Date.now();

const isBlockedByNonMembershipAvailability = (promotion: Models<"PromotionModel">) =>
  !promotion.active ||
  promotion.isDiscountUsed ||
  isPromotionExpired(promotion) ||
  isPromotionNotYetStarted(promotion);

/**
 * Whether the offer's own Apply/Redeem action may run. A membership-locked promotion answers
 * `false` here and is not actionable *as an offer* — its own "join the membership" CTA is decided
 * by `isMembershipLockedPromotion`, which stays a separate question so the detail view can keep
 * merchandising a locked offer while refusing to apply it.
 */
export const isPromotionActionable = (promotion: Models<"PromotionModel">) =>
  isPromotionAvailableToPlatformUser(promotion) &&
  !isPromotionNotYetStarted(promotion) &&
  !isPromotionExpired(promotion);

export const isMembershipLockedPromotion = (promotion: Models<"PromotionModel">) =>
  !isPromotionAvailableToPlatformUser(promotion) &&
  !isBlockedByNonMembershipAvailability(promotion) &&
  getRequiredMembershipIds(promotion).length > 0;

/**
 * Whether this person has any door into the promotion at all: its own Apply/Redeem, or the "join
 * the membership" CTA of a lock. A promotion outside its window, spent, or deactivated answers
 * `false` — nothing about it can be acted on, so surfaces that would sell a way in (the primary
 * CTA, the per-offer membership disclosure) stand down rather than advertise a closed campaign.
 */
export const isPromotionOpenForAction = (promotion: Models<"PromotionModel">) =>
  isMembershipLockedPromotion(promotion) || isPromotionActionable(promotion);

const MEMBERSHIP_WORD = /membership/i;

const formatMembershipName = (membershipName?: string | null) => {
  if (!membershipName) return null;
  return MEMBERSHIP_WORD.test(membershipName)
    ? membershipName
    : i18next.t("promotionPresentation.membershipSuffix", { name: membershipName });
};

/**
 * The required memberships the catalog can name, each still carrying its id so a surface listing
 * them can also link to each one's product page.
 */
export const getMembershipRows = (
  membershipIds: string[],
  memberships?: Models<"MembershipModel">[],
) =>
  membershipIds
    .map((membershipId) => {
      const membership = memberships?.find(({ id }) => id === membershipId);
      return { id: membershipId, name: formatMembershipName(membership?.name) };
    })
    .filter((row): row is { id: string; name: string } => !!row.name);

const getMembershipDisplayNames = (
  membershipIds: string[],
  memberships?: Models<"MembershipModel">[],
) => getMembershipRows(membershipIds, memberships).map(({ name }) => name);

export const getRequiredMembershipDisplayNames = (
  promotion: Models<"PromotionModel">,
  memberships?: Models<"MembershipModel">[],
) =>
  getMembershipDisplayNames(getJoinableRequiredMembershipIds(promotion, memberships), memberships);

/**
 * The required memberships someone could join today. A members-only offer covers every version of
 * a membership, so it also stores the ids of retired versions; only the live version is in the
 * shop catalog, and a retired one can be neither bought nor linked to, so it is neither counted
 * nor named. When none of them is in the catalog (an offer on a retired-only membership, or an
 * unlisted one), every stored id stands, as it does until the catalog has loaded.
 */
export const getJoinableMembershipIds = (
  membershipIds: string[],
  memberships?: Models<"MembershipModel">[],
) => {
  if (memberships === undefined) return membershipIds;
  const onSale = membershipIds.filter((membershipId) =>
    memberships.some(({ id }) => id === membershipId),
  );
  return onSale.length > 0 ? onSale : membershipIds;
};

export const getJoinableRequiredMembershipIds = (
  promotion: Models<"PromotionModel">,
  memberships?: Models<"MembershipModel">[],
) => getJoinableMembershipIds(getRequiredMembershipIds(promotion), memberships);

/**
 * The one-line "who this is for" name list every membership message shares: the single name, both
 * names, or `null` when the catalog cannot name them all — callers then use their generic copy
 * rather than advertising a partial list as the full set.
 */
const summarizeMembershipNames = (
  membershipIds: string[],
  memberships?: Models<"MembershipModel">[],
) => {
  const membershipNames = getMembershipDisplayNames(membershipIds, memberships);

  if (membershipIds.length === 1 && membershipNames.length === 1) {
    return membershipNames[0];
  }

  if (membershipIds.length === 2 && membershipNames.length === 2) {
    return i18next.t("promotionPresentation.membershipNamePair", {
      name1: membershipNames[0],
      name2: membershipNames[1],
    });
  }

  return null;
};

const getMembershipJoinAction = (
  membershipIds: string[],
  memberships?: Models<"MembershipModel">[],
) => {
  if (membershipIds.length === 0) return null;

  if (membershipIds.length === 1) {
    const membership = memberships?.find(({ id }) => id === membershipIds[0]);
    const membershipName = formatMembershipName(membership?.name);

    return {
      text: membershipName
        ? i18next.t("promotionPresentation.joinMembership", { name: membershipName })
        : i18next.t("promotionPresentation.joinMembershipGeneric"),
      to: `/pdp/membership/${membershipIds[0]}`,
    };
  }

  return {
    text: i18next.t("promotionPresentation.shopMemberships"),
    to: "/shop/memberships",
  };
};

/**
 * The join line under one gated offer inside a campaign. A single required membership has one
 * product page to send someone to, so it stays a link. Several do not: the offer opens to whichever
 * of them this person joins, and the whole-shop link the promotion-level CTA falls back to loses
 * that list on the way. Those open the eligible-memberships sheet over this offer's own ids.
 */
export type CampaignOfferJoinAction =
  | { kind: "link"; text: string; to: string }
  | { kind: "sheet"; text: string; membershipIds: string[] };

const getCampaignOfferJoinAction = (
  requiredMembershipIds: string[],
  memberships?: Models<"MembershipModel">[],
): CampaignOfferJoinAction | null => {
  const membershipIds = getJoinableMembershipIds(requiredMembershipIds, memberships);
  if (membershipIds.length <= 1) {
    const joinLink = getMembershipJoinAction(membershipIds, memberships);
    return joinLink === null ? null : { kind: "link", ...joinLink };
  }

  // A pair the catalog can name reads as both names. Everything else — three or more, or a pair
  // with a name missing — falls to the count, which stays true either way rather than advertising a
  // partial list as the whole set.
  const membershipNames = summarizeMembershipNames(membershipIds, memberships);
  return {
    kind: "sheet",
    text: membershipNames
      ? i18next.t("promotionPresentation.joinMembership", { name: membershipNames })
      : i18next.t("promotionPresentation.joinOneOfEligibleMemberships", {
          count: membershipIds.length,
        }),
    membershipIds,
  };
};

export const getMembershipLockedPromotionAction = (
  promotion: Models<"PromotionModel">,
  memberships?: Models<"MembershipModel">[],
) => getMembershipJoinAction(getJoinableRequiredMembershipIds(promotion, memberships), memberships);

export const getMembershipLockedPromotionNotice = (
  promotion: Models<"PromotionModel">,
  memberships?: Models<"MembershipModel">[],
) => {
  const membershipIds = getJoinableRequiredMembershipIds(promotion, memberships);
  if (membershipIds.length === 0) return null;
  const membershipNames = getRequiredMembershipDisplayNames(promotion, memberships);

  if (membershipIds.length === 1) {
    return membershipNames[0]
      ? i18next.t("promotionPresentation.exclusiveToOne", { name: membershipNames[0] })
      : i18next.t("promotionPresentation.exclusiveToGeneric");
  }

  if (membershipIds.length === 2 && membershipNames.length === 2) {
    return i18next.t("promotionPresentation.exclusiveToTwo", {
      name1: membershipNames[0],
      name2: membershipNames[1],
    });
  }

  return i18next.t("promotionPresentation.exclusiveToCount", { count: membershipIds.length });
};

export const getMembershipUnlockedPromotionNotice = (
  promotion: Models<"PromotionModel">,
  memberships?: Models<"MembershipModel">[],
) => {
  // Every required id, not just the ones on sale: this speaks to a member about what they hold,
  // which may be a retired version, so it names a plan only when the offer requires exactly one.
  const membershipIds = getRequiredMembershipIds(promotion);
  if (membershipIds.length === 0) return null;
  const membershipNames = getMembershipDisplayNames(membershipIds, memberships);

  if (membershipIds.length === 1) {
    return membershipNames[0]
      ? i18next.t("promotionPresentation.unlocksOne", { name: membershipNames[0] })
      : i18next.t("promotionPresentation.unlocksGeneric");
  }

  if (membershipIds.length === 2 && membershipNames.length === 2) {
    return i18next.t("promotionPresentation.unlocksTwo", {
      name1: membershipNames[0],
      name2: membershipNames[1],
    });
  }

  return i18next.t("promotionPresentation.unlocksEligible");
};

/**
 * Promotion-level membership copy, which only a single-offer promotion gets. Every sentence it can
 * produce — "exclusive to", "unlocks this offer" — is about one offer's requirement, so on a
 * campaign carrying several it would describe one offer while sitting above them all. There the
 * per-offer badges say which offer wants which membership, and nothing speaks for the campaign.
 */
export const getMembershipPromotionDetailNotice = (
  promotion: Models<"PromotionModel">,
  memberships?: Models<"MembershipModel">[],
) => {
  if (!isSingleOfferPromotion(promotion)) return null;

  if (isMembershipLockedPromotion(promotion)) {
    return getMembershipLockedPromotionNotice(promotion, memberships);
  }

  if (hasMembershipRequirement(promotion)) {
    return getMembershipUnlockedPromotionNotice(promotion, memberships);
  }

  return null;
};

/**
 * What one offer inside a campaign is to this person:
 *
 * - `open` — claimable with no membership story to tell.
 * - `claimableUnmatchedMembership` — claimable, yet gated by memberships none of which is in the
 *   held set; renders exactly like `open`.
 * - `includedInMembership` — claimable *because* of a membership they hold, which the tag names.
 * - `membersOnly` — withheld for a reason we can name, so it is merchandised with a badge and a way
 *   to join rather than silently dropped. The membership is named by the join action, not the
 *   badge: the badge shares the offer's name row and has room for one short phrase.
 * - `hidden` — withheld for a reason this surface cannot name (location, schedule, redemption
 *   limit); saying nothing beats explaining it wrongly.
 */
export type CampaignOfferMembershipPresentation = {
  status:
    | "open"
    | "claimableUnmatchedMembership"
    | "includedInMembership"
    | "membersOnly"
    | "hidden";
  tag: string | null;
  joinAction: CampaignOfferJoinAction | null;
};

/** No membership story to tell — also what an offer shows before the held set is known. */
export const OPEN_CAMPAIGN_OFFER: CampaignOfferMembershipPresentation = {
  status: "open",
  tag: null,
  joinAction: null,
};

/**
 * Claimable per the backend, yet none of the memberships it requires is in the held set. Known
 * divergence sources: clock skew either side of an expiration boundary, and Zenoti-managed
 * memberships excluded from both lists. A render must not throw on it, so it stays untagged.
 */
const CLAIMABLE_UNMATCHED_MEMBERSHIP: CampaignOfferMembershipPresentation = {
  status: "claimableUnmatchedMembership",
  tag: null,
  joinAction: null,
};

const HIDDEN_OFFER: CampaignOfferMembershipPresentation = {
  status: "hidden",
  tag: null,
  joinAction: null,
};

export const getCampaignOfferMembershipPresentation = (
  promotion: Models<"PromotionModel">,
  promoCode: Models<"PromoCodeModel">,
  heldMembershipIds: string[],
  memberships?: Models<"MembershipModel">[],
): CampaignOfferMembershipPresentation => {
  const requiredMembershipIds = getPromoCodeRequiredMembershipIds(promoCode);
  // The backend lists only the offers this person may claim; everything else in `promoCodes` is
  // live but withheld, and the requirement below is what lets us say why.
  const isClaimable = promotion.eligiblePromoCodeIds.includes(promoCode.id);
  const heldMembershipId = requiredMembershipIds.find((membershipId) =>
    heldMembershipIds.includes(membershipId),
  );

  if (isClaimable) {
    if (heldMembershipId !== undefined) {
      return {
        status: "includedInMembership",
        tag: getIncludedInMembershipTag(heldMembershipId, memberships),
        joinAction: null,
      };
    }

    return requiredMembershipIds.length > 0 ? CLAIMABLE_UNMATCHED_MEMBERSHIP : OPEN_CAMPAIGN_OFFER;
  }

  if (requiredMembershipIds.length > 0 && heldMembershipId === undefined) {
    return {
      status: "membersOnly",
      tag: i18next.t("promotionPresentation.memberExclusiveBadge"),
      joinAction: getCampaignOfferJoinAction(requiredMembershipIds, memberships),
    };
  }

  return HIDDEN_OFFER;
};

const getIncludedInMembershipTag = (
  heldMembershipId: string,
  memberships?: Models<"MembershipModel">[],
) => {
  const membershipName = summarizeMembershipNames([heldMembershipId], memberships);
  return membershipName
    ? i18next.t("promotionPresentation.includedInMembership", { name: membershipName })
    : i18next.t("promotionPresentation.includedInMembershipGeneric");
};
